/**
 * GAU: instant feedback for cart quantity changes.
 * The moment a customer taps + / - (or types a quantity) we update the line price, the struck-through
 * old price and the footer total on screen, BEFORE the server answers. This is visual only: nothing is
 * sent from here, and when the theme's own update finishes it replaces everything with the real numbers.
 * Fully wrapped in try/catch - if anything is unexpected it simply does nothing.
 *
 * Also: tapping "Estimated total" in the drawer opens the Price Summary (details.gcx-ps) instead of the old pop-up,
 * and the Price Summary keeps its open/closed state when the drawer refreshes after a cart change.
 */
const NUM = /\d[\d,]*(?:\.\d+)?/;
const totals = new WeakMap();

/** @param {string} text */
function parse(text) {
  const m = NUM.exec(text || '');
  if (!m) return null;
  return {
    num: parseFloat(m[0].replace(/,/g, '')),
    dec: (m[0].split('.')[1] || '').length,
    comma: m[0].includes(','),
  };
}

/**
 * @param {number} n
 * @param {{dec:number, comma:boolean, num:number}} p
 */
function format(n, p) {
  return Math.max(0, n).toLocaleString('en-IN', {
    minimumFractionDigits: p.dec,
    maximumFractionDigits: p.dec,
    useGrouping: p.comma || p.num < 1000,
  });
}

/**
 * Rewrites the number inside `el`, keeping its currency symbol and decimals.
 * @param {HTMLElement} el
 * @param {number} ratio
 * @returns {number | null} the change in the number, or null when nothing was changed
 */
function scale(el, ratio) {
  if (!el.dataset.gcxOrig) el.dataset.gcxOrig = el.textContent.trim();
  const orig = el.dataset.gcxOrig;
  const p = parse(orig);
  if (!p) return null;
  const next = Math.round(p.num * ratio * 100) / 100;
  el.textContent = orig.replace(NUM, () => format(next, p));
  return next - p.num;
}

/** @param {HTMLInputElement} input */
function apply(input) {
  try {
    const row = input.closest('.cart-card');
    if (!row) return;
    const cartQty = parseInt(input.getAttribute('data-cart-quantity') ?? input.defaultValue, 10);
    const qty = parseInt(input.value, 10);
    if (!(cartQty > 0) || !(qty > 0)) return;
    const ratio = qty / cartQty;

    const priceEl = row.querySelector('.cart-card__price text-component');
    if (!(priceEl instanceof HTMLElement)) return;
    const delta = scale(priceEl, ratio);
    if (delta === null) return;

    const strike = row.querySelector('.cart-card__price-strike');
    if (strike instanceof HTMLElement) scale(strike, ratio);

    const scope = row.closest('cart-items-component') || document;
    const total = scope.querySelector('[data-cart-subtotal]') || document.querySelector('[data-cart-subtotal]');
    if (!(total instanceof HTMLElement)) return;

    const current = total.textContent.trim();
    let state = totals.get(total);
    if (!state || state.written !== current) {
      state = { written: current, base: parse(current), deltas: new Map() };
      totals.set(total, state);
    }
    if (!state.base) return;
    state.deltas.set(row.dataset.key || '', delta);
    let sum = 0;
    state.deltas.forEach((d) => (sum += d));
    const text = current.replace(NUM, () => format(state.base.num + sum, state.base));
    total.textContent = text;
    state.written = text;
  } catch (error) {
    /* visual only - ignore */
  }
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('.cart-card .quantity-minus, .cart-card .quantity-plus');
    if (!button) return;
    const host = button.closest('quantity-selector-component, cart-quantity-selector-component') || button.parentElement;
    const input = host?.querySelector('input');
    if (!(input instanceof HTMLInputElement)) return;
    // let the selector update its number first
    setTimeout(() => apply(input), 40);
  },
  true
);

document.addEventListener('change', (event) => {
  const input = event.target;
  if (input instanceof HTMLInputElement && input.closest('.cart-card .quantity-selector, .cart-card quantity-selector-component')) {
    apply(input);
  }
});

/* ---------- Price Summary (drawer) ---------- */
let psOpen = false;
let psQueued = false;

// "Estimated total" opens / closes the Price Summary instead of the old pop-up
document.addEventListener(
  'click',
  (event) => {
    try {
      const target = event.target instanceof Element ? event.target : null;
      const toggle = target?.closest('.redesigned-cart-drawer .cart-footer-total__toggle');
      if (!toggle) return;
      const ps = toggle.closest('.cart-drawer__summary')?.querySelector('details.gcx-ps');
      if (!(ps instanceof HTMLDetailsElement)) return;
      event.preventDefault();
      ps.open = !ps.open;
    } catch (error) {
      /* ignore */
    }
  },
  true
);

// remember whether the Price Summary is open
document.addEventListener(
  'toggle',
  (event) => {
    const el = event.target;
    if (el instanceof HTMLDetailsElement && el.classList.contains('gcx-ps')) psOpen = el.open;
  },
  true
);

// after the drawer refreshes (new HTML), put it back the way the customer left it
new MutationObserver(() => {
  if (psQueued) return;
  psQueued = true;
  requestAnimationFrame(() => {
    psQueued = false;
    try {
      const ps = document.querySelector('.redesigned-cart-drawer details.gcx-ps');
      if (ps instanceof HTMLDetailsElement && ps.open !== psOpen) ps.open = psOpen;
    } catch (error) {
      /* ignore */
    }
  });
}).observe(document.body, { childList: true, subtree: true });
